import { useMemo, useRef, useState } from 'react'
import type {
  AudienceImportPreviewProjection,
  AudienceImportSegmentFiltersColumnGroup,
} from '@digital-twins/shared-types'
import type {
  FilterChipGroup,
  FilterChipItem,
} from '@/types/components/audiences/filter-chip-grid'
import type { SegmentationCategory } from '@/types/components/audiences/segmentation-mapping'
import {
  IDENTITY_CANONICAL_IDS,
  getCategoryForCanonicalId,
  getDisplayLabel,
} from '@/types/components/audiences/segmentation-mapping'
import { toOperationalSet } from '@/layouts/AudienceImportSessionLayout/lib/operational-columns'
import {
  buildSegmentFilterQuestions,
  type SegmentFilterQuestion,
} from '@/lib/utils/segment-filter-questions'

/**
 * Mirror of the backend's isFreeTextColumnLabel check.
 * Columns whose label or name indicates an open-ended free-text response are
 * not categorical and cannot be meaningfully used as audience filters.
 *
 * Keep in sync with FREETEXT_LABEL_PATTERNS in population-filter.service.ts.
 */
function isFreeTextLabel(label: string): boolean {
  const norm = (label ?? '').trim().toLowerCase()
  if (!norm) return false
  // Exact-match standalone labels
  if (/^freetext$/.test(norm)) return true
  if (/^free\s*text$/.test(norm)) return true
  if (/^open\s*text$/.test(norm)) return true
  if (/^open\s*end(?:ed)?$/.test(norm)) return true
  if (/^verbatim$/.test(norm)) return true
  if (/^comments?$/.test(norm)) return true
  if (/^text\s*response$/.test(norm)) return true
  if (/^open\s*response$/.test(norm)) return true
  // "other" + specify/please anywhere in label
  if (/^other\b.*\bspecify\b/.test(norm)) return true
  if (/^other\b.*\bplease\b/.test(norm)) return true
  // Suffix patterns: "...: Freetext", "... - Open text", etc.
  if (/[\s:\-]+freetext\s*$/.test(norm)) return true
  if (/[\s:\-]+free\s*text\s*$/.test(norm)) return true
  if (/[\s:\-]+open\s*text\s*$/.test(norm)) return true
  if (/[\s:\-]+open\s*end(?:ed)?\s*$/.test(norm)) return true
  if (/[\s:\-]+other\s*specify\s*$/.test(norm)) return true
  if (/[\s:\-]+other\s*\(specify\)\s*$/.test(norm)) return true
  if (/[\s:\-]+other\s*\(please\s*specify\)\s*$/.test(norm)) return true
  if (/[\s:\-]+other[\s,\-]+please\s*specify\s*$/.test(norm)) return true
  if (/[\s:\-]+verbatim\s*$/.test(norm)) return true
  return false
}

/**
 * Mirror of the backend's isDerivedNonFilterableColumn check (keyed by the raw
 * originalName). Derived companion columns — red-herring attention checks and
 * one-hot grid-cell dummies — collide on humanized labels with real questions
 * and render as meaningless binary batteries, so they must never be filterable.
 *
 * Keep in sync with isDerivedNonFilterableColumn in
 * apps/backend/src/populations/utils/derived-columns.ts.
 */
function isDerivedNonFilterableColumn(
  originalName: string | null | undefined,
): boolean {
  const name = (originalName ?? '').trim().toLowerCase()
  if (!name) return false
  if (/^d_redherring/.test(name)) return true
  if (/^d_/.test(name) && /r\d+c\d+/.test(name)) return true
  return false
}

export function buildFilterChipItems(
  mappings: AudienceImportPreviewProjection['segmentationMappings'],
  samples: Record<string, string[]>,
  customLabels: Record<string, string>,
): FilterChipItem[] {
  const chips = mappings
    .filter((mapping) => !mapping.isOperational)
    .filter((mapping) => {
      // Exclude free-text open-ended columns — they are not categorical and
      // cannot be meaningfully filtered. Mirrors the backend's check so these
      // columns never appear as choosable in the segmentation step.
      const label = mapping.displayLabel ?? mapping.sourceColumn
      return !isFreeTextLabel(label) && !isFreeTextLabel(mapping.sourceColumn)
    })
    .filter(
      // Exclude derived red-herring / one-hot dummy columns (by originalName).
      (mapping) =>
        !isDerivedNonFilterableColumn(
          (mapping as { originalName?: string | null }).originalName,
        ),
    )
    .map((mapping) => {
      const canonicalLabel = mapping.canonicalId
        ? getDisplayLabel(mapping.canonicalId)
        : null

      const resolvedDisplayLabel =
        customLabels[mapping.sourceColumn] ??
        canonicalLabel ??
        mapping.displayLabel ??
        mapping.sourceColumn

      return {
        sourceColumn: mapping.sourceColumn,
        displayLabel: resolvedDisplayLabel,
        originalLabel:
          mapping.canonicalId && IDENTITY_CANONICAL_IDS.has(mapping.canonicalId)
            ? resolvedDisplayLabel
            : mapping.displayLabel,
        canonicalId: mapping.canonicalId,
        category: getCategoryForCanonicalId(mapping.canonicalId) ?? null,
        sampleValues: samples[mapping.sourceColumn]?.slice(0, 5) ?? [],
        isLocked:
          mapping.canonicalId != null &&
          IDENTITY_CANONICAL_IDS.has(mapping.canonicalId),
        valueLabels: mapping.valueLabels,
        valueMap: mapping.valueMap,
      }
    })

  const bestLockedByCanonical = new Map<string, string>()
  for (const chip of chips) {
    if (!chip.isLocked || !chip.canonicalId) continue
    const existing = bestLockedByCanonical.get(chip.canonicalId)
    if (!existing) {
      bestLockedByCanonical.set(chip.canonicalId, chip.sourceColumn)
      continue
    }
    const existingChip = chips.find((c) => c.sourceColumn === existing)
    if (existingChip && existingChip.sampleValues.length === 0 && chip.sampleValues.length > 0) {
      bestLockedByCanonical.set(chip.canonicalId, chip.sourceColumn)
    }
  }

  return chips.filter((chip) => {
    // Locked canonical chips are de-duped: only keep the best representative.
    if (chip.isLocked && chip.canonicalId) {
      return bestLockedByCanonical.get(chip.canonicalId) === chip.sourceColumn
    }
    // Non-locked explicitly-mapped columns are always shown — the backend
    // decides which columns are filterable; the frontend should not second-guess
    // it with a heuristic sample count guard.
    return true
  })
}

export function computeDefaultSelectedFilters(items: FilterChipItem[]): string[] {
  const seenCanonical = new Set<string>()
  const locked = items.filter((item) => item.isLocked).map((item) => item.sourceColumn)

  items
    .filter((item) => item.isLocked && item.canonicalId)
    .forEach((item) => seenCanonical.add(item.canonicalId!))

  const canonical = items
    .filter((item) => {
      if (item.isLocked || !item.canonicalId) return false
      if (seenCanonical.has(item.canonicalId)) return false
      seenCanonical.add(item.canonicalId)
      return true
    })
    .map((item) => item.sourceColumn)

  return [...locked, ...canonical]
}

export function resolveActiveFilterColumns(
  filterSelection: string[] | null | undefined,
  items: FilterChipItem[],
): string[] {
  if (filterSelection != null) {
    if (filterSelection.length === 0) return []
    const validColumns = new Set(items.map((item) => item.sourceColumn))
    const sanitized = filterSelection.filter((column) => validColumns.has(column))
    return sanitized.length > 0 || items.length === 0
      ? sanitized
      : computeDefaultSelectedFilters(items)
  }
  if (items.length === 0) return []
  return computeDefaultSelectedFilters(items)
}

/**
 * Compute the active filter column list by excluding hidden columns from all eligible items.
 * Used by the "Filters to Hide" wizard flow: all columns are active except explicitly hidden ones.
 */
export function computeActiveFromHidden(
  hiddenColumns: string[],
  items: FilterChipItem[],
): string[] {
  if (hiddenColumns.length === 0) {
    return items.map((item) => item.sourceColumn)
  }
  const hiddenSet = new Set(hiddenColumns)
  return items
    .filter((item) => !hiddenSet.has(item.sourceColumn))
    .map((item) => item.sourceColumn)
}

interface UsePreviewFilterSelectionOptions {
  /** External persistent state from wizard context. When provided, the hook
   *  syncs mutations to these rather than managing internal state. */
  persistedSelection?: string[] | null
  onSelectionChange?: (selection: string[] | null) => void
  persistedLabels?: Record<string, string>
  onLabelsChange?: (labels: Record<string, string>) => void
}

export function usePreviewFilterSelection(
  projection: AudienceImportPreviewProjection | null,
  options?: UsePreviewFilterSelectionOptions,
) {
  const [localSelection, setLocalSelection] = useState<string[] | null>(null)
  const [localLabels, setLocalLabels] = useState<Record<string, string>>({})
  const [detailItem, setDetailItem] = useState<FilterChipItem | null>(null)
  const [detailGroup, setDetailGroup] = useState<FilterChipGroup | null>(null)
  const [addModalOpen, setAddModalOpen] = useState(false)

  const filterSelection = options?.persistedSelection !== undefined
    ? options.persistedSelection
    : localSelection
  const setFilterSelection = options?.onSelectionChange ?? setLocalSelection
  const customLabels = options?.persistedLabels ?? localLabels
  const setCustomLabels = options?.onLabelsChange ?? setLocalLabels
  const customLabelsRef = useRef(customLabels)
  customLabelsRef.current = customLabels

  const filterChipItems = useMemo(
    () =>
      projection?.segmentationMappings
        ? buildFilterChipItems(
            projection.segmentationMappings,
            projection.filterColumnSamples ?? {},
            customLabels,
          )
        : [],
    [projection?.segmentationMappings, projection?.filterColumnSamples, customLabels],
  )

  /** Columns with a non-operational segmentation mapping.  These bypass the
   *  operational gating so QUOTA demographic columns (classified operational
   *  by Phase 0 but mapped to a canonical field) remain eligible. */
  const mappedSourceColumns = useMemo(() => {
    const set = new Set<string>()
    for (const m of projection?.segmentationMappings ?? []) {
      if (!m.isOperational) set.add(m.sourceColumn)
    }
    return set
  }, [projection?.segmentationMappings])

  /** Segment-eligible columns: prefer catalog-backed candidates when present. */
  const eligibleSegmentSourceColumns = useMemo(() => {
    const candidates = projection?.segmentFilterCandidates
    if (candidates?.length) {
      const set = new Set<string>()
      for (const c of candidates) {
        if (!c.isOperational && !c.isDropped) {
          set.add(c.sourceColumn)
        } else if (mappedSourceColumns.has(c.sourceColumn) && !c.isDropped) {
          set.add(c.sourceColumn)
        }
      }
      return set
    }
    const set = new Set<string>()
    for (const m of projection?.segmentationMappings ?? []) {
      if (!m.isOperational) {
        set.add(m.sourceColumn)
      }
    }
    return set
  }, [projection?.segmentFilterCandidates, projection?.segmentationMappings, mappedSourceColumns])

  /** Preflight operational column set (shared across samples, groups, and mappings). */
  const operationalSet = useMemo(
    () => toOperationalSet(projection?.segmentFiltersPreflight?.operationalColumns),
    [projection?.segmentFiltersPreflight?.operationalColumns],
  )

  const columnGroupsForAddFilter = useMemo(():
    | Array<AudienceImportSegmentFiltersColumnGroup>
    | undefined => {
    const preflight = projection?.segmentFiltersPreflight
    const groups = preflight?.columnGroups
    if (!groups?.length) return undefined
    const filtered = groups
      .map((g) => ({
        ...g,
        columns: g.columns.filter(
          (c) => eligibleSegmentSourceColumns.has(c) && (!operationalSet.has(c) || mappedSourceColumns.has(c)),
        ),
      }))
      .filter((g) => g.columns.length > 0)
    return filtered.length > 0 ? filtered : undefined
  }, [projection?.segmentFiltersPreflight, eligibleSegmentSourceColumns, operationalSet, mappedSourceColumns])

  const activeFilterColumns = useMemo(() => {
    return resolveActiveFilterColumns(filterSelection, filterChipItems)
  }, [filterSelection, filterChipItems])

  /** Flat eligible columns fed to the shared question builder.
   *
   * Includes both explicitly-mapped chip items AND catalog-only candidates from
   * segmentFilterCandidates that passed the eligibility check but have no
   * segmentation mapping yet — so they appear in the Add-filter modal.
   * Catalog-only candidates with < 2 distinct sample values are excluded here;
   * mapped chip items are included unconditionally (the backend decides eligibility).
   */
  const filterColumnInputs = useMemo(() => {
    const fromChips = filterChipItems
      .filter((chip) => {
        // Operational-noise columns (e.g. "Record number", "Participant
        // identifier", "Completion time and date") sometimes carry an
        // auto-generated passthrough mapping (canonicalId === null,
        // isOperational === false) which lets them leak into the filter list via
        // the chip path. The operational bypass is intended only for QUOTA
        // demographic columns that Phase 0 flagged operational but were mapped to
        // a canonical field, so keep an operational column only when it is
        // genuinely canonical-mapped; drop pure passthrough mappings.
        if (operationalSet.has(chip.sourceColumn) && chip.canonicalId == null) {
          return false
        }
        return true
      })
      .map((chip) => ({
        sourceColumn: chip.sourceColumn,
        normalizedLabel: chip.displayLabel,
        originalLabel: chip.originalLabel,
        category: (chip.category as SegmentationCategory | null) ?? null,
        sampleValues: chip.sampleValues,
        valueLabels: chip.valueLabels,
        valueMap: chip.valueMap,
      }))
    const mappedSet = new Set(filterChipItems.map((c) => c.sourceColumn))
    const samples = projection?.filterColumnSamples ?? {}
    const catalogCols = (projection?.segmentFilterCandidates ?? [])
      .filter((c) => {
        if (mappedSet.has(c.sourceColumn)) return false
        if (!eligibleSegmentSourceColumns.has(c.sourceColumn)) return false
        // Drop preflight operational/noise columns (e.g. "record number", captured
        // variables) that are listed in operationalColumns even when their candidate
        // isOperational flag is unset, unless the user explicitly mapped them. Mirrors
        // the dev filterCleanColumns(..., operationalSet, mappedSourceColumns) guard.
        if (operationalSet.has(c.sourceColumn) && !mappedSourceColumns.has(c.sourceColumn)) {
          return false
        }
        if (c.isDropped) return false
        if (isFreeTextLabel(c.displayLabel) || isFreeTextLabel(c.sourceColumn)) return false
        const distinctCount = new Set(
          (samples[c.sourceColumn] ?? [])
            .map((v) => String(v).trim().toLowerCase())
            .filter(Boolean),
        ).size
        return distinctCount >= 2
      })
      .map((c) => ({
        sourceColumn: c.sourceColumn,
        normalizedLabel: c.displayLabel,
        originalLabel: c.originalLabel,
        category: getCategoryForCanonicalId(c.canonicalId) ?? null,
        sampleValues: (samples[c.sourceColumn] ?? []).slice(0, 5),
      }))
    return [...fromChips, ...catalogCols]
  }, [filterChipItems, projection?.segmentFilterCandidates, projection?.filterColumnSamples, eligibleSegmentSourceColumns, operationalSet, mappedSourceColumns])

  /** All questions over the full eligible universe (single + grouped). */
  const allQuestions = useMemo(
    () => buildSegmentFilterQuestions(filterColumnInputs, columnGroupsForAddFilter),
    [filterColumnInputs, columnGroupsForAddFilter],
  )

  /** Questions still addable: drop singles already added and fully-added groups. */
  const filterQuestions = useMemo(() => {
    const selected = new Set(activeFilterColumns)
    return allQuestions.filter((q) =>
      q.kind === 'single'
        ? !selected.has(q.options[0].sourceColumn)
        : !q.options.every((o) => selected.has(o.sourceColumn)),
    )
  }, [allQuestions, activeFilterColumns])

  /** Grouped filters for the chip grid — member columns collapse into one chip. */
  const filterGroups = useMemo<FilterChipGroup[]>(
    () =>
      allQuestions
        .filter((q) => q.kind === 'group')
        .map((q) => ({
          groupId: q.id,
          label: customLabels[q.id] ?? q.label,
          memberColumns: q.options.map((o) => o.sourceColumn),
          options: q.options.map((o) => ({
            sourceColumn: o.sourceColumn,
            optionLabel: o.optionLabel,
            sampleValues: o.sampleValues,
            valueLabels: o.valueLabels,
            valueMap: o.valueMap,
          })),
        })),
    [allQuestions, customLabels],
  )

  return {
    filterChipItems,
    columnGroupsForAddFilter,
    activeFilterColumns,
    detailItem,
    setDetailItem,
    detailGroup,
    setDetailGroup,
    addModalOpen,
    setAddModalOpen,
    filterQuestions,
    /** Full filterable question universe (singles + groups), regardless of active state. */
    allQuestions,
    filterGroups,
    removeFilter(sourceColumn: string) {
      setFilterSelection(activeFilterColumns.filter((col) => col !== sourceColumn))
    },
    renameFilter(sourceColumn: string, newLabel: string) {
      setCustomLabels({ ...customLabelsRef.current, [sourceColumn]: newLabel })
    },
    /** Remove a whole group — drops every member column from the selection. */
    removeGroup(groupId: string) {
      const group = filterGroups.find((g) => g.groupId === groupId)
      if (!group) return
      const members = new Set(group.memberColumns)
      setFilterSelection(activeFilterColumns.filter((col) => !members.has(col)))
    },
    /** Rename a group; label is stored under the group id.
     *  An empty string clears the alias (reverts to the auto-derived stem). */
    renameGroup(groupId: string, newLabel: string) {
      if (!newLabel) {
        const { [groupId]: _removed, ...rest } = customLabelsRef.current
        setCustomLabels(rest)
      } else {
        setCustomLabels({ ...customLabelsRef.current, [groupId]: newLabel })
      }
    },
    /** Add a question or group as a single logical unit (all member columns). */
    addQuestion(question: SegmentFilterQuestion, customLabel?: string) {
      const cols = question.options.map((o) => o.sourceColumn)
      const existing = new Set(activeFilterColumns)
      const newCols = cols.filter((col) => !existing.has(col))
      if (newCols.length > 0) {
        setFilterSelection([...activeFilterColumns, ...newCols])
      }
      const trimmed = customLabel?.trim()
      if (trimmed) {
        const key =
          question.kind === 'group' ? question.id : question.options[0].sourceColumn
        setCustomLabels({ ...customLabelsRef.current, [key]: trimmed })
      }
    },
    addFilter(sourceColumn: string, customLabel?: string) {
      setFilterSelection([...activeFilterColumns, sourceColumn])
      if (customLabel) {
        setCustomLabels({ ...customLabelsRef.current, [sourceColumn]: customLabel })
      }
    },
  }
}
